import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Send, Sparkles, ExternalLink, Loader2, Mail, Award, TrendingUp, Clock, BookmarkPlus, BookmarkCheck, Share2, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const MATCH_COLORS = {
  "STRONG MATCH":  { bg: "#DCFCE7", fg: "#065F46" },
  "GOOD MATCH":    { bg: "#FEF9C3", fg: "#92400E" },
  "ALTERNATIVE":   { bg: "#DBEAFE", fg: "#1E40AF" },
};

const courseKey  = (c) => (c?.name || "").trim().toLowerCase();
const collegeKey = (c) => `${(c?.name || "").trim().toLowerCase()}|${(c?.location || "").trim().toLowerCase()}`;

function Pill({ label }) {
  const c = MATCH_COLORS[label] || { bg: "#F1F5F9", fg: "#334155" };
  return <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: c.bg, color: c.fg }}>{label}</span>;
}

function Section({ n, title, children, action }) {
  return (
    <section className="mt-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] tracking-[0.32em] font-bold" style={{ color: "#B98700" }}>SECTION {n}</span>
        <span className="h-px flex-1" style={{ background: "#E7DCB9" }}/>
        {action}
      </div>
      <h2 className="font-editorial text-2xl md:text-3xl font-bold text-slate-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function buildWhatsappUrl(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export default function AiAdvisorReportPage() {
  const { sid } = useParams();
  const [data, setData] = useState(null);
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(10);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [shortlist, setShortlist] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    api.get(`/advisor/sessions/${sid}`).then(r => {
      setData(r.data);
      setChat(r.data.chat_history || []);
      setRemaining(r.data.chat_remaining);
      setShortlist(r.data.shortlist || []);
      if (r.data.student_name) setName(r.data.student_name);
      if (r.data.email) setEmail(r.data.email);
      if (r.data.email_sent_at) setEmailSaved(true);
    }).catch(() => toast.error("Session not found"));
  }, [sid]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    if (remaining <= 0) { toast.error("Session message limit reached"); return; }
    const msg = input.trim();
    setInput("");
    setChat(c => [...c, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data: r } = await api.post(`/advisor/sessions/${sid}/chat`, { message: msg });
      setChat(c => [...c, { role: "assistant", content: r.reply, citations: r.citations }]);
      setRemaining(r.remaining);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chat failed");
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    if (!name.trim()) { toast.error("Please tell us your name first."); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Please enter a valid email address."); return; }
    setEmailBusy(true);
    try {
      const { data: r } = await api.post(`/advisor/sessions/${sid}/email`, { email: email.trim(), name: name.trim() });
      setEmailSaved(true);
      if (r?.sent === false) {
        toast.success("Saved. We'll send it as soon as the mailer is live.");
      } else {
        toast.success(`Your report is on the way to ${email.trim()}.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setEmailBusy(false); }
  };

  const toggleShortlist = async (kind, key, payload) => {
    // optimistic update
    const idx = shortlist.findIndex(s => s.kind === kind && s.key === key);
    let next;
    if (idx >= 0) next = shortlist.filter((_, i) => i !== idx);
    else next = [...shortlist, { kind, key, payload }];
    setShortlist(next);
    try {
      await api.post(`/advisor/sessions/${sid}/shortlist`, { kind, key, payload });
    } catch (e) {
      setShortlist(shortlist); // revert
      toast.error("Failed to update shortlist");
    }
  };

  const isSaved = (kind, key) => shortlist.some(s => s.kind === kind && s.key === key);

  const shareOnWhatsapp = (label, subject) => {
    const url = `${window.location.origin}/ai-advisor/report/${sid}`;
    const text = `${subject}\n\nCheck out my personalised EduTime AI Education Advisor report: ${url}`;
    window.open(buildWhatsappUrl(text), "_blank", "noopener,noreferrer");
    // No toast on window.open — the new tab is the signal.
    if (label) { /* label used for testids only */ }
  };

  const shareCollegeOnWhatsapp = (c) => {
    const url = `${window.location.origin}/ai-advisor/report/${sid}`;
    const text = `EduTime AI suggested ${c.name}${c.location ? ` (${c.location})` : ""} for me${c.programme ? ` — Programme: ${c.programme}` : ""}. See my full AI report: ${url}`;
    window.open(buildWhatsappUrl(text), "_blank", "noopener,noreferrer");
  };

  const savedCourses = useMemo(() => shortlist.filter(s => s.kind === "course"), [shortlist]);
  const savedColleges = useMemo(() => shortlist.filter(s => s.kind === "college"), [shortlist]);

  if (!data) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400">Loading…</div>;

  const report = data.report || {};
  const parseFailed = report.parse_failed;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="advisor-report">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-amber-600"/>
        <span className="overline text-amber-700">YOUR EDUTIME AI EDUCATION ADVISOR REPORT</span>
      </div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
        <h1 className="font-editorial text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Your personalised guidance.</h1>
        <button
          onClick={() => shareOnWhatsapp("report", "Just got my personalised education report from EduTime AI!")}
          className="self-start md:self-end inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white text-sm font-semibold shadow-sm"
          data-testid="share-report-whatsapp">
          <MessageCircle className="w-4 h-4"/> Share report on WhatsApp
        </button>
      </div>
      <p className="text-slate-600 max-w-2xl">Based on the information you provided, here is what our AI Advisor found.</p>

      {parseFailed && (
        <div className="mt-6 p-5 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
          The AI returned a free-form response. Showing it as-is below:
          <pre className="mt-3 whitespace-pre-wrap font-sans">{report.raw_text}</pre>
        </div>
      )}

      {/* Section 1 — Profile */}
      {!parseFailed && report.profile_summary && (
        <Section n="1" title="Your Profile">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {Object.entries(report.profile_summary).map(([k, v]) => (
              <div key={k} className="card-soft p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{k.replace(/_/g, " ")}</div>
                <div className="text-slate-900 font-semibold">{Array.isArray(v) ? v.join(", ") : (v || "—")}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Section 2 — Courses */}
      {!parseFailed && report.courses?.length > 0 && (
        <Section n="2" title="Your Course Recommendations">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {report.courses.map((c, i) => {
              const k = courseKey(c);
              const saved = isSaved("course", k);
              return (
                <div key={i} className="card-soft p-5 relative" data-testid={`report-course-${i}`}>
                  <button
                    onClick={() => toggleShortlist("course", k, { name: c.name, why_it_suits: c.why_it_suits, typical_eligibility: c.typical_eligibility, approximate_duration: c.approximate_duration, career_opportunities: c.career_opportunities })}
                    className={`absolute top-3 right-3 p-2 rounded-full text-xs font-semibold flex items-center gap-1 ${saved ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    title={saved ? "Remove from shortlist" : "Save to shortlist"}
                    data-testid={`save-course-${i}`}>
                    {saved ? <BookmarkCheck className="w-4 h-4"/> : <BookmarkPlus className="w-4 h-4"/>}
                  </button>
                  <h3 className="font-editorial text-lg font-bold text-slate-900 mb-2 pr-10">{c.name}</h3>
                  <p className="text-sm text-slate-700 mb-3">{c.why_it_suits}</p>
                  <dl className="text-xs space-y-1.5 text-slate-600">
                    <div><dt className="inline font-bold text-slate-800">Career: </dt><dd className="inline">{c.career_opportunities}</dd></div>
                    <div><dt className="inline font-bold text-slate-800">Eligibility: </dt><dd className="inline">{c.typical_eligibility}</dd></div>
                    <div><dt className="inline font-bold text-slate-800">Duration: </dt><dd className="inline">{c.approximate_duration}</dd></div>
                    <div><dt className="inline font-bold text-slate-800">Considerations: </dt><dd className="inline">{c.considerations}</dd></div>
                  </dl>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Section 3 — Colleges */}
      {!parseFailed && report.colleges?.length > 0 && (
        <Section n="3" title="Recommended Colleges & Universities">
          <div className="space-y-4">
            {report.colleges.map((c, i) => {
              const k = collegeKey(c);
              const saved = isSaved("college", k);
              return (
                <div key={i} className="card-soft p-5 flex flex-col md:flex-row md:items-start gap-4 relative" data-testid={`report-college-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-editorial text-xl font-bold text-slate-900">{c.name}</h3>
                      <Pill label={c.match_label}/>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3 h-3"/> {c.location}</div>
                    <div className="text-sm text-slate-700 mb-2"><strong>Programme:</strong> {c.programme}</div>
                    <p className="text-sm text-slate-700"><strong>Why it may suit:</strong> {c.why_it_suits}</p>
                    <p className="text-xs text-slate-500 mt-2 italic">{c.considerations}</p>
                  </div>
                  <div className="md:w-64 shrink-0 text-xs text-slate-600 space-y-1.5">
                    <div><TrendingUp className="w-3 h-3 inline mr-1"/> <strong>Fees:</strong> {c.approximate_fees}</div>
                    <div><Award className="w-3 h-3 inline mr-1"/> <strong>Eligibility:</strong> {c.eligibility}</div>
                    {c.source_url && (
                      <a href={c.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:underline font-semibold text-[11px]">
                        Official site <ExternalLink className="w-3 h-3"/>
                      </a>
                    )}
                    <div className="pt-2 flex flex-wrap gap-2">
                      <button
                        onClick={() => toggleShortlist("college", k, { name: c.name, location: c.location, programme: c.programme, match_label: c.match_label, approximate_fees: c.approximate_fees, eligibility: c.eligibility, why_it_suits: c.why_it_suits, source_url: c.source_url })}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold ${saved ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                        data-testid={`save-college-${i}`}>
                        {saved ? <><BookmarkCheck className="w-3.5 h-3.5"/> Saved</> : <><BookmarkPlus className="w-3.5 h-3.5"/> Save</>}
                      </button>
                      <button
                        onClick={() => shareCollegeOnWhatsapp(c)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-[#25D366] hover:bg-[#1EBE5A] text-white"
                        data-testid={`share-college-${i}`}>
                        <MessageCircle className="w-3.5 h-3.5"/> Share
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Section 4 — Why */}
      {!parseFailed && report.why_these_options && (
        <Section n="4" title="Why These Options?">
          <div className="card-soft p-5 text-sm text-slate-700 leading-relaxed">{report.why_these_options}</div>
        </Section>
      )}

      {/* Section 5 — Compare (AI-generated) */}
      {!parseFailed && report.comparison?.length > 0 && (
        <Section n="5" title="Compare Your Options">
          <div className="card-soft overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left">
                  {["Institution","Course","Location","Approx. Fees","Eligibility","Career Orientation","Overall Suitability"].map(h => (
                    <th key={h} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.comparison.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-semibold text-slate-900">{r.institution}</td>
                    <td className="px-4 py-3 text-slate-700">{r.course}</td>
                    <td className="px-4 py-3 text-slate-500">{r.location}</td>
                    <td className="px-4 py-3 text-slate-700">{r.approx_fees}</td>
                    <td className="px-4 py-3 text-slate-500">{r.eligibility}</td>
                    <td className="px-4 py-3 text-slate-500">{r.career_orientation}</td>
                    <td className="px-4 py-3 text-slate-700">{r.overall_suitability}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Citations */}
      {!!data.citations?.length && (
        <div className="mt-8 card-soft p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Sources checked by AI</div>
          <ul className="text-sm text-sky-700 space-y-1">
            {data.citations.slice(0, 8).map((c, i) => (
              <li key={i}><a href={c.url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">{c.title || c.url}<ExternalLink className="w-3 h-3"/></a></li>
            ))}
          </ul>
        </div>
      )}

      {/* Transparency notice */}
      <div className="mt-8 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
        <Clock className="w-3.5 h-3.5 inline mr-1 text-slate-500"/>
        <strong>Information checked:</strong> {new Date().toISOString().slice(0, 10)}. EduTime AI recommendations are based on the information you provided and publicly available information researched by the AI. They are intended to help you explore your options and should not be considered a guarantee of admission, employment or career outcome. Always verify current fees, eligibility, admissions, accreditation and programme details with the institution.
      </div>

      {/* Email opt-in */}
      {!emailSaved && (
        <div className="mt-6 card-soft p-5 space-y-3" data-testid="report-email-block">
          <div>
            <div className="text-sm font-bold text-slate-900 mb-1">Want a copy by email?</div>
            <div className="text-xs text-slate-500">We'll send the report to your inbox and you can return anytime.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Your name</label>
              <input type="text" placeholder="e.g. Priya Sharma" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white"
                data-testid="report-name"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Your email</label>
              <input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white"
                data-testid="report-email"/>
            </div>
          </div>
          <button onClick={sendEmail} disabled={emailBusy || !name.trim() || !email.trim()}
            className="px-5 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-bold disabled:opacity-50"
            data-testid="report-email-save">
            {emailBusy ? <><Loader2 className="w-4 h-4 inline mr-1 animate-spin"/>Sending…</> : <><Mail className="w-4 h-4 inline mr-1"/>Send me my report</>}
          </button>
        </div>
      )}
      {emailSaved && (
        <div className="mt-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" data-testid="report-email-sent">
          <Mail className="w-4 h-4 inline mr-1"/> Your report has been sent to <strong>{email}</strong>.
        </div>
      )}

      {/* Follow-up chat */}
      <div className="mt-10 card-soft p-5" data-testid="advisor-chat">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-editorial text-xl font-bold text-slate-900">Ask EduTime AI</h3>
          <span className="text-xs text-slate-500">{remaining} messages left</span>
        </div>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {chat.length === 0 && (
            <div className="text-xs text-slate-500">
              Try: "Can you suggest cheaper options?" · "Which option is better for placements?" · "Compare X and Y" · "Can you suggest options abroad?"
            </div>
          )}
          {chat.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-slate-900 text-yellow-300" : "bg-slate-100 text-slate-800"}`}>
                {m.content.split("\n").map((line, j) => <p key={j} className="mb-1">{line}</p>)}
                {!!m.citations?.length && (
                  <div className="mt-2 text-[10px] text-slate-600">Sources: {m.citations.slice(0, 3).map((c, j) => <a key={j} href={c.url} target="_blank" rel="noreferrer" className="underline mr-2">{c.title || "link"}</a>)}</div>
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef}/>
        </div>
        <form onSubmit={send} className="mt-4 flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} disabled={busy || remaining <= 0}
            placeholder={remaining <= 0 ? "Session limit reached" : "Ask a follow-up question…"}
            className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white"
            data-testid="chat-input"/>
          <button disabled={busy || remaining <= 0} className="px-5 py-2.5 rounded-full bg-slate-900 text-yellow-300 text-sm font-bold disabled:opacity-50" data-testid="chat-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          </button>
        </form>
      </div>

      {/* Floating shortlist pill */}
      {shortlist.length > 0 && !compareOpen && (
        <button
          onClick={() => setCompareOpen(true)}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-yellow-300 text-sm font-bold shadow-xl hover:bg-slate-800"
          data-testid="open-compare">
          <BookmarkCheck className="w-4 h-4"/> Compare my shortlist ({shortlist.length})
        </button>
      )}

      {/* Compare modal */}
      {compareOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setCompareOpen(false)}>
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl p-6 my-6" onClick={e => e.stopPropagation()} data-testid="compare-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-amber-700 mb-1">MY SHORTLIST</div>
                <h2 className="font-editorial text-2xl md:text-3xl font-bold text-slate-900">Side-by-side comparison</h2>
              </div>
              <button onClick={() => setCompareOpen(false)} className="p-2 rounded-full hover:bg-slate-100" data-testid="close-compare"><X className="w-5 h-5"/></button>
            </div>

            {savedColleges.length > 0 && (
              <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Colleges ({savedColleges.length})</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-slate-100 rounded-xl overflow-hidden">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2.5">Attribute</th>
                        {savedColleges.map((s, i) => (
                          <th key={i} className="px-3 py-2.5 min-w-[220px]">
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-bold text-slate-900 normal-case tracking-normal">{s.payload?.name}</span>
                              <button onClick={() => toggleShortlist("college", s.key, s.payload)} className="p-1 rounded-full hover:bg-slate-200" title="Remove"><X className="w-3.5 h-3.5"/></button>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-slate-700">
                      {[
                        ["Match", (s) => s.payload?.match_label || "—"],
                        ["Location", (s) => s.payload?.location || "—"],
                        ["Programme", (s) => s.payload?.programme || "—"],
                        ["Approx. Fees", (s) => s.payload?.approximate_fees || "—"],
                        ["Eligibility", (s) => s.payload?.eligibility || "—"],
                        ["Why it may suit", (s) => s.payload?.why_it_suits || "—"],
                        ["Official site", (s) => s.payload?.source_url ? <a href={s.payload.source_url} target="_blank" rel="noreferrer" className="text-sky-700 underline">Visit</a> : "—"],
                      ].map(([label, fn], r) => (
                        <tr key={r} className="border-t border-slate-100 align-top">
                          <td className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{label}</td>
                          {savedColleges.map((s, i) => (
                            <td key={i} className="px-3 py-2.5">{typeof fn === "function" ? fn(s) : "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {savedCourses.length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Courses ({savedCourses.length})</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-slate-100 rounded-xl overflow-hidden">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2.5">Attribute</th>
                        {savedCourses.map((s, i) => (
                          <th key={i} className="px-3 py-2.5 min-w-[220px]">
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-bold text-slate-900 normal-case tracking-normal">{s.payload?.name}</span>
                              <button onClick={() => toggleShortlist("course", s.key, s.payload)} className="p-1 rounded-full hover:bg-slate-200" title="Remove"><X className="w-3.5 h-3.5"/></button>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-slate-700">
                      {[
                        ["Why it suits", (s) => s.payload?.why_it_suits || "—"],
                        ["Duration", (s) => s.payload?.approximate_duration || "—"],
                        ["Eligibility", (s) => s.payload?.typical_eligibility || "—"],
                        ["Career", (s) => s.payload?.career_opportunities || "—"],
                      ].map(([label, fn], r) => (
                        <tr key={r} className="border-t border-slate-100 align-top">
                          <td className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{label}</td>
                          {savedCourses.map((s, i) => (
                            <td key={i} className="px-3 py-2.5">{typeof fn === "function" ? fn(s) : "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {savedColleges.length === 0 && savedCourses.length === 0 && (
              <div className="py-12 text-center text-slate-500 text-sm">Your shortlist is empty. Tap the Save icon on any course or college above to compare them here.</div>
            )}

            <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-slate-100">
              <button
                onClick={() => shareOnWhatsapp("shortlist", `Here's my shortlist of ${savedColleges.length} college(s) and ${savedCourses.length} course(s) from EduTime AI:`)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-semibold"
                data-testid="share-shortlist-whatsapp">
                <Share2 className="w-3.5 h-3.5"/> Share shortlist on WhatsApp
              </button>
              <button onClick={() => setCompareOpen(false)} className="ml-auto px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
